import { graphql, useStaticQuery } from 'gatsby';
import { RefObject, useMemo } from 'react';
import { IContentfulImage, IContentfulVideo, IStaticContentfulImages, IStaticMixedQuery } from 'src/types/contentful';
import { Post } from 'src/app/molecules/FeedPostSlider/FeedPostAssetCarousel';
import useLazyLoadedURL from './useLazyLoadedURL';

type ProductKey = 'community' | 'courses' | 'members' | 'marketing' | 'payments' | 'admin' | 'aiCohost';

interface UseProductFeedPostsOptions {
  exclude?: ProductKey[];
  containerRef: RefObject<HTMLDivElement>;
  omitHrefs?: boolean;
}

const useProductFeedPosts = ({ exclude = [], containerRef, omitHrefs = false }: UseProductFeedPostsOptions): Post[] => {
  const assets = useStaticQuery<IStaticMixedQuery<IContentfulImage, IContentfulVideo> & IStaticContentfulImages>(
    graphql`
      {
        communityVideo: contentfulAsset(contentful_id: { eq: "1j4ruPD5NPccDsiYNnKOo5" }) {
          localFile {
            publicURL
          }
        }
        communityPoster: contentfulAsset(contentful_id: { eq: "6ksht0rripSSLFH7y3fc4g" }) {
          localFile {
            publicURL
          }
        }
        courses: contentfulAsset(contentful_id: { eq: "6NxJ8RhkkrBH107cuDtdKo" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
        members: contentfulAsset(contentful_id: { eq: "2uan74EeE3guUPAnNDlzjD" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
        marketing: contentfulAsset(contentful_id: { eq: "23TlE8iR50Pieer058ZnP" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
        payments: contentfulAsset(contentful_id: { eq: "1xmnWnOCTbM8LA7ePt9xLW" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
        admin: contentfulAsset(contentful_id: { eq: "6EFpAdCGc2cm08V8mhux4c" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
        aiCohost: contentfulAsset(contentful_id: { eq: "3b3AzrYf1EudD629RH55hi" }) {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1800)
        }
      }
    `
  );

  const videoSource = useLazyLoadedURL(containerRef, assets.communityVideo.localFile.publicURL, 0.1);

  const allPosts = useMemo<Record<ProductKey, Post>>(
    () => ({
      community: {
        href: '/community',
        video: {
          src: videoSource,
          poster: assets.communityPoster.localFile.publicURL,
          autoPlay: true,
          muted: true,
          loop: true,
          playsInline: true,
          controls: false,
        },
        cover: true,
        imageMaxWidth: 336,
        title: 'Community',
      },
      courses: {
        href: '/online-courses',
        image: {
          image: assets.courses.gatsbyImageData,
          alt: 'Courses',
        },
        imageMaxWidth: 336,
        title: 'Courses',
      },
      members: {
        href: '/members',
        image: {
          image: assets.members.gatsbyImageData,
          alt: 'Members',
        },
        imageMaxWidth: 336,
        title: 'Members',
      },
      marketing: {
        href: '/marketing',
        image: {
          image: assets.marketing.gatsbyImageData,
          alt: 'Marketing',
        },
        imageMaxWidth: 336,
        title: 'Marketing',
      },
      payments: {
        href: '/payments',
        image: {
          image: assets.payments.gatsbyImageData,
          alt: 'Payments',
        },
        imageMaxWidth: 336,
        title: 'Payments',
      },
      admin: {
        href: '/admin',
        image: {
          image: assets.admin.gatsbyImageData,
          alt: 'Admin',
        },
        imageMaxWidth: 336,
        title: 'Admin',
      },
      aiCohost: {
        href: '/ai',
        image: {
          image: assets.aiCohost.gatsbyImageData,
          alt: 'AI Cohost',
        },
        imageMaxWidth: 336,
        title: 'AI Cohost',
      },
    }),
    [videoSource, assets]
  );

  const filteredPosts = useMemo(() => {
    const allKeys: ProductKey[] = ['community', 'courses', 'members', 'marketing', 'payments', 'admin', 'aiCohost'];
    const posts = allKeys.filter((key) => !exclude.includes(key)).map((key) => allPosts[key]);
    if (omitHrefs) {
      return posts.map(({ href: _href, ...rest }) => rest);
    }
    return posts;
  }, [allPosts, exclude, omitHrefs]);

  return filteredPosts;
};

export default useProductFeedPosts;
